/**
 * User Batch Service
 *
 * Centralized service for efficient user profile lookups across the application.
 *
 * Features:
 * - Batch fetching: Get multiple user profiles in 2 queries instead of N queries
 * - In-memory caching: 5-minute TTL to avoid duplicate lookups
 * - Auto-detection: Queries both students and teachers tables
 * - Cache invalidation: Automatic cleanup of expired entries
 *
 * Usage:
 * ```javascript
 * import { batchGetUserDetails, getCachedUserDetails } from './services/userBatchService';
 *
 * // Batch fetch multiple users
 * const userIds = ['uuid-1', 'uuid-2', 'uuid-3'];
 * const usersMap = await batchGetUserDetails(userIds);
 * // Returns: { 'uuid-1': {name, avatar, role}, 'uuid-2': {...}, ... }
 *
 * // Single user with caching
 * const user = await getCachedUserDetails('uuid-1');
 * // Returns: {name, avatar, role}
 * ```
 */

import { supabase } from '../supabaseClient';

// ============================================
// In-Memory Cache
// ============================================

const userCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Clean up expired cache entries
 * Runs automatically every minute
 */
function cleanupExpiredCache() {
  const now = Date.now();
  let removedCount = 0;

  for (const [userId, cacheEntry] of userCache.entries()) {
    if (now - cacheEntry.timestamp > CACHE_TTL) {
      userCache.delete(userId);
      removedCount++;
    }
  }

  if (removedCount > 0) {
    console.log(`[UserBatchService] Cleaned up ${removedCount} expired cache entries`);
  }
}

// Auto-cleanup every minute
setInterval(cleanupExpiredCache, 60 * 1000);


// ============================================
// Batch User Fetching
// ============================================

/**
 * Batch fetch user details for multiple user IDs
 *
 * This function fetches users from both students and teachers tables in parallel,
 * eliminating the N+1 query problem where each user lookup requires 2 queries.
 *
 * Performance:
 * - Before: N users × 2 queries = 2N queries
 * - After: 2 queries total (1 for students, 1 for teachers)
 *
 * @param {string[]} userIds - Array of user UUID strings
 * @returns {Promise<Object>} Map of userId -> {name, avatar, role, email}
 *
 * @example
 * const userIds = ['abc-123', 'def-456'];
 * const users = await batchGetUserDetails(userIds);
 * console.log(users['abc-123'].name); // "John Doe"
 */
export async function batchGetUserDetails(userIds) {
  // Validate input
  if (!userIds || !Array.isArray(userIds) || userIds.length === 0) {
    return {};
  }

  // Remove duplicates and filter out null/undefined
  const uniqueUserIds = [...new Set(userIds.filter(id => id))];

  if (uniqueUserIds.length === 0) {
    return {};
  }

  console.log(`[UserBatchService] Batch fetching ${uniqueUserIds.length} users`);

  try {
    // Parallel queries to both students and teachers tables
    const [studentsResult, teachersResult] = await Promise.all([
      supabase
        .from('students')
        .select('full_name, profile_photo, uuid_id, email')
        .in('uuid_id', uniqueUserIds),

      supabase
        .from('Teachers')
        .select('full_name, profile_photo, uuid_id, email')
        .in('uuid_id', uniqueUserIds)
    ]);

    // Build user details map
    const userMap = {};

    // Process students
    if (studentsResult.data) {
      studentsResult.data.forEach(student => {
        userMap[student.uuid_id] = {
          name: student.full_name || 'Student',
          avatar: student.profile_photo || null,
          role: 'student',
          email: student.email || null
        };

        // Update cache
        userCache.set(student.uuid_id, {
          data: userMap[student.uuid_id],
          timestamp: Date.now()
        });
      });
    }

    // Process teachers
    if (teachersResult.data) {
      teachersResult.data.forEach(teacher => {
        userMap[teacher.uuid_id] = {
          name: teacher.full_name || 'Teacher',
          avatar: teacher.profile_photo || null,
          role: 'teacher',
          email: teacher.email || null
        };

        // Update cache
        userCache.set(teacher.uuid_id, {
          data: userMap[teacher.uuid_id],
          timestamp: Date.now()
        });
      });
    }

    // Log warnings for missing users
    const foundUserIds = Object.keys(userMap);
    const missingUserIds = uniqueUserIds.filter(id => !foundUserIds.includes(id));
    if (missingUserIds.length > 0) {
      console.warn(`[UserBatchService] Could not find ${missingUserIds.length} users:`, missingUserIds);
    }

    console.log(`[UserBatchService] Successfully fetched ${foundUserIds.length}/${uniqueUserIds.length} users`);

    return userMap;

  } catch (error) {
    console.error('[UserBatchService] Error in batchGetUserDetails:', error);
    return {};
  }
}


// ============================================
// Single User Lookup with Caching
// ============================================

/**
 * Get user details for a single user ID with caching
 *
 * Checks cache first, only queries database if not cached or cache expired.
 * Ideal for individual user lookups in components.
 *
 * @param {string} userId - User UUID string
 * @returns {Promise<Object>} User details {name, avatar, role, email}
 *
 * @example
 * const user = await getCachedUserDetails('abc-123');
 * console.log(user.name); // "Jane Smith"
 */
export async function getCachedUserDetails(userId) {
  if (!userId) {
    return {
      name: 'Unknown User',
      avatar: null,
      role: null,
      email: null
    };
  }

  // Check cache first
  const cached = userCache.get(userId);
  const now = Date.now();

  if (cached && (now - cached.timestamp) < CACHE_TTL) {
    console.log(`[UserBatchService] Cache hit for user ${userId}`);
    return cached.data;
  }

  console.log(`[UserBatchService] Cache miss for user ${userId}, fetching...`);

  // Cache miss - fetch from database
  const users = await batchGetUserDetails([userId]);
  const userData = users[userId] || {
    name: 'Unknown User',
    avatar: null,
    role: null,
    email: null
  };

  return userData;
}


// ============================================
// Cache Management
// ============================================

/**
 * Clear the entire user cache
 * Useful for testing or when you need to force fresh data
 *
 * @example
 * clearUserCache();
 */
export function clearUserCache() {
  const size = userCache.size;
  userCache.clear();
  console.log(`[UserBatchService] Cleared cache (${size} entries removed)`);
}

/**
 * Remove a specific user from cache
 * Useful when a user profile is updated
 *
 * @param {string} userId - User UUID to remove from cache
 *
 * @example
 * invalidateUserCache('abc-123');
 */
export function invalidateUserCache(userId) {
  if (userCache.has(userId)) {
    userCache.delete(userId);
    console.log(`[UserBatchService] Invalidated cache for user ${userId}`);
  }
}

/**
 * Get cache statistics
 * Useful for debugging and monitoring
 *
 * @returns {Object} Cache stats {size, entries}
 *
 * @example
 * const stats = getCacheStats();
 * console.log(`Cache size: ${stats.size}`);
 */
export function getCacheStats() {
  const now = Date.now();
  const entries = [];

  for (const [userId, cacheEntry] of userCache.entries()) {
    entries.push({
      userId,
      age: Math.round((now - cacheEntry.timestamp) / 1000), // seconds
      data: cacheEntry.data
    });
  }

  return {
    size: userCache.size,
    ttl: CACHE_TTL / 1000, // seconds
    entries
  };
}


// ============================================
// Batch Lookup by Email
// ============================================

/**
 * Batch fetch user details by email addresses
 *
 * Similar to batchGetUserDetails but uses email as lookup key.
 * Useful for booking requests and notifications.
 *
 * @param {string[]} emails - Array of email addresses
 * @returns {Promise<Object>} Map of email -> {name, avatar, role, uuid_id}
 *
 * @example
 * const emails = ['student@example.com', 'teacher@example.com'];
 * const users = await batchGetUserDetailsByEmail(emails);
 * console.log(users['student@example.com'].name);
 */
export async function batchGetUserDetailsByEmail(emails) {
  // Validate input
  if (!emails || !Array.isArray(emails) || emails.length === 0) {
    return {};
  }

  // Remove duplicates and filter out null/undefined
  const uniqueEmails = [...new Set(emails.filter(email => email))];

  if (uniqueEmails.length === 0) {
    return {};
  }

  console.log(`[UserBatchService] Batch fetching ${uniqueEmails.length} users by email`);

  try {
    // Parallel queries to both students and teachers tables
    const [studentsResult, teachersResult] = await Promise.all([
      supabase
        .from('students')
        .select('full_name, profile_photo, uuid_id, email')
        .in('email', uniqueEmails),

      supabase
        .from('Teachers')
        .select('full_name, profile_photo, uuid_id, email')
        .in('email', uniqueEmails)
    ]);

    // Build user details map by email
    const userMap = {};

    // Process students
    if (studentsResult.data) {
      studentsResult.data.forEach(student => {
        userMap[student.email] = {
          name: student.full_name || 'Student',
          avatar: student.profile_photo || null,
          role: 'student',
          uuid_id: student.uuid_id
        };

        // Also update UUID cache
        userCache.set(student.uuid_id, {
          data: userMap[student.email],
          timestamp: Date.now()
        });
      });
    }

    // Process teachers
    if (teachersResult.data) {
      teachersResult.data.forEach(teacher => {
        userMap[teacher.email] = {
          name: teacher.full_name || 'Teacher',
          avatar: teacher.profile_photo || null,
          role: 'teacher',
          uuid_id: teacher.uuid_id
        };

        // Also update UUID cache
        userCache.set(teacher.uuid_id, {
          data: userMap[teacher.email],
          timestamp: Date.now()
        });
      });
    }

    console.log(`[UserBatchService] Successfully fetched ${Object.keys(userMap).length}/${uniqueEmails.length} users by email`);

    return userMap;

  } catch (error) {
    console.error('[UserBatchService] Error in batchGetUserDetailsByEmail:', error);
    return {};
  }
}


// ============================================
// Export All Functions
// ============================================

export default {
  batchGetUserDetails,
  getCachedUserDetails,
  batchGetUserDetailsByEmail,
  clearUserCache,
  invalidateUserCache,
  getCacheStats
};
